<template>
  <b-modal :title="importText('title')"
           dialog-class="modal-lg"
           :modelValue="isVisible"
           footer-class="d-flex justify-content-end gap-2"
           @update:modelValue="onVisibleChange">
    <template #default>
      <LoaderWrapper ref="loader" class="h-100">
        <div class="p-3 pt-0">
          <div v-if="!allowlistEnabled" class="row mt-2">
            <label class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans('counterparty_import.select_requisite') }}</label>
            <div class="col-sm-8">
              <Multiselect
                v-model="selectedRequisite"
                :options="requisiteOptions"
                :custom-label="requisiteLabel"
                track-by="id"
                select-label="Select"
                :placeholder="helper.trans('labels.id')"
                open-direction="bottom"
                :multiple="false"
                :searchable="true"
                :loading="requisitesLoading"
                :internal-search="false"
                :clear-on-select="true"
                :close-on-select="true"
                :options-limit="100"
                :show-no-results="true"
                @search-change="asyncFindRequisites"
              >
              </Multiselect>
            </div>
          </div>

          <div class="row mt-2">
            <label class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans('counterparty_import.select_store') }}</label>
            <div class="col-sm-8">
              <Multiselect
                v-model="selectedStore"
                :options="storeOptions"
                :custom-label="storeLabel"
                track-by="id"
                select-label="Select"
                :placeholder="helper.trans('labels.id')"
                open-direction="bottom"
                :multiple="false"
                :searchable="true"
                :loading="storesLoading"
                :internal-search="false"
                :clear-on-select="true"
                :close-on-select="true"
                :options-limit="100"
                :show-no-results="true"
                @search-change="asyncFindStores"
              >
              </Multiselect>
            </div>
          </div>

          <div class="row mt-3">
            <label class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans('counterparty_import.file_requirements_title') }}</label>
            <div class="col-sm-8">
              <ul class="mb-1 ps-3 small text-muted">
                <li>{{ helper.trans('counterparty_import.file_requirements_utf8') }}</li>
                <li>{{ helper.trans('counterparty_import.file_requirements_line') }}</li>
                <li>{{ helper.trans('counterparty_import.file_requirements_empty') }}</li>
              </ul>
              <pre class="bg-light border rounded p-2 mb-0 small">user_abc123
user_def456
user_ghi789</pre>
            </div>
          </div>

          <div class="row mt-3">
            <label class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans('counterparty_import.choose_file') }}</label>
            <div class="col-sm-8">
              <div v-if="!selectedFile"
                   style="cursor: pointer; border:1px dashed"
                   v-bind="dropzone.getRootProps()"
                   class="payout-dropzone rounded border-secondary p-3 pt-4 pb-4 d-flex justify-content-center align-items-center">
                <input v-bind="dropzone.getInputProps()" />
                <p class="p-0 m-0">{{ helper.trans('counterparty_import.dropzone') }}</p>
              </div>
              <div v-else>
                <i class="fa fa-paperclip"></i>
                <span class="me-2">{{ selectedFile.name }}</span>
                <a href="#" @click.prevent="clearFile">
                  <i class="fa fa-times"></i>
                </a>
              </div>
            </div>
          </div>

          <div v-if="result" class="row mt-3">
            <div class="col-sm-12">
              <div class="d-flex flex-wrap gap-3 mb-2">
                <span>{{ helper.trans('counterparty_import.in_file', { n: result.total }) }}</span>
                <span>{{ importText('to_import', { n: result.toImport }) }}</span>
                <span v-if="!allowlistEnabled && result.freeSlots !== null && result.freeSlots !== undefined">{{ helper.trans('counterparty_import.free_slots', { n: result.freeSlots }) }}</span>
              </div>

              <div v-if="result.valid && result.toImport === 0" class="alert alert-info py-2 px-3">
                {{ importText('nothing_to_import') }}
              </div>

              <div v-if="result.alreadyBound && result.alreadyBound.length" class="alert alert-warning py-2 px-3">
                <div class="fw-semibold mb-1">{{ importText('already_bound') }}</div>
                <div class="small text-break">{{ result.alreadyBound.join(', ') }}</div>
              </div>

              <div v-if="result.duplicatesInFile && result.duplicatesInFile.length" class="alert alert-danger py-2 px-3">
                <div class="fw-semibold mb-1">{{ helper.trans('counterparty_import.duplicates_in_file') }}</div>
                <div class="small text-break">{{ result.duplicatesInFile.join(', ') }}</div>
              </div>

              <div v-if="visibleErrors.length" class="alert alert-danger py-2 px-3 mb-0">
                <ul class="mb-0 ps-3" style="max-height: 180px; overflow: auto">
                  <li v-for="(error, index) in visibleErrors" :key="index">
                    {{ formatError(error) }}
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <p class="small text-muted mt-3 mb-0">{{ importText('slot_hint') }}</p>
        </div>
      </LoaderWrapper>
    </template>
    <template #footer>
      <button type="button" @click.prevent="hide" class="btn btn-outline-secondary btn-sm text-capitalize-first">
        {{ helper.trans('button.close') }}
      </button>
      <button type="button"
              :disabled="!canCheck || isBusy"
              @click.prevent="validateFile"
              class="btn btn-outline-primary btn-sm text-capitalize-first">
        {{ helper.trans('button.check_file') }}
      </button>
      <button type="button"
              :disabled="!canImport || isBusy"
              @click.prevent="importFile"
              class="btn btn-outline-primary btn-sm text-capitalize-first">
        {{ helper.trans('labels.import') }}
      </button>
    </template>
  </b-modal>
</template>

<script lang="ts">
import {computed, defineComponent, ref, watch} from 'vue'
import {useStore} from "vuex";
import {debounce, helper} from "@/service/CommonHelper";
import APIClient from "@/service/APIClient";
import {BModal} from "bootstrap-vue-3";
import LoaderWrapper from "@/components/LoaderWrapper.vue";
import Multiselect from "vue-multiselect";
import TraderRequisitesDTO from "@/models/TraderRequisitesDTO";
import MerchantStoreDTO from "@/models/MerchantStoreDTO";
import PaginationDTO from "@/models/PaginationDTO";
import RequisitesRequestDTO from "@/models/request/RequisitesRequestDTO";
import CounterpartyImportResultDTO, {CounterpartyImportErrorDTO} from "@/models/CounterpartyImportResultDTO";
import {useDropzone} from "vue3-dropzone";
import {useNotification} from "@kyvg/vue3-notification";

const api = new APIClient();
const notification = useNotification();

const REASON_KEYS: Record<string, string> = {
  'File is empty': 'counterparty_import.reasons.file_empty',
  'Duplicate in file': 'counterparty_import.reasons.duplicate_in_file',
}

const NOT_ENOUGH_SLOTS = /Not enough free slots: need (\d+), free (\d+)/

export default defineComponent({
  components: {
    LoaderWrapper,
    BModal,
    Multiselect,
  },
  emits: ['imported'],
  setup(props, {emit}) {
    const store = useStore()
    const allowlistEnabled = computed(() => !!store.getters.getAppData?.STORE_COUNTERPARTY_ALLOWLIST_ENABLED)

    const importText = (key: string, params?: Record<string, string | number>) => {
      const suffix = allowlistEnabled.value ? '_allowlist' : ''
      return helper.trans(`counterparty_import.${key}${suffix}`, params)
    }
    const loader = ref<InstanceType<typeof LoaderWrapper> | null>(null)
    const isVisible = ref(false)
    const isBusy = ref(false)
    const requisitesLoading = ref(false)
    const storesLoading = ref(false)
    const requisites = ref<TraderRequisitesDTO[]>([])
    const stores = ref<MerchantStoreDTO[]>([])
    const selectedRequisite = ref<TraderRequisitesDTO | null>(null)
    const selectedStore = ref<MerchantStoreDTO | null>(null)
    const selectedFile = ref<File | null>(null)
    const result = ref<CounterpartyImportResultDTO | null>(null)

    const requisiteLabel = (opt: TraderRequisitesDTO): string => {
      if (!opt) {
        return ''
      }
      const parts = [String(opt.id)]
      if (opt.requisites) {
        parts.push(opt.requisites)
      }
      if (opt.holderName) {
        parts.push(opt.holderName)
      }
      return parts.join(' · ')
    }

    const requisiteOptions = computed(() => {
      const list = [...requisites.value]
      if (selectedRequisite.value && !list.find((item) => item.id === selectedRequisite.value!.id)) {
        list.unshift(selectedRequisite.value)
      }
      return list
    })

    const storeLabel = (opt: MerchantStoreDTO): string => {
      if (!opt) {
        return ''
      }
      const name = [opt.merchantName, opt.name].filter(Boolean).join(' - ')
      const parts = [String(opt.id)]
      if (name) {
        parts.push(name)
      }
      if (opt.currency) {
        parts.push(opt.currency)
      }
      return parts.join(' · ')
    }

    const storeOptions = computed(() => {
      const list = [...stores.value]
      if (selectedStore.value && !list.find((item) => item.id === selectedStore.value!.id)) {
        list.unshift(selectedStore.value)
      }
      return list
    })

    const selectedStoreId = computed(() => selectedStore.value?.id ?? null)

    const canCheck = computed(() => {
      if (!selectedStoreId.value || !selectedFile.value) {
        return false
      }
      return allowlistEnabled.value || !!selectedRequisite.value
    })

    const canImport = computed(() => {
      return canCheck.value && result.value?.valid === true
    })

    const visibleErrors = computed(() => {
      const errors = result.value?.errors ?? []
      if (!allowlistEnabled.value) {
        return errors
      }
      return errors.filter((error) => !NOT_ENOUGH_SLOTS.test(error.reason))
    })

    const loadRequisites = async (query: string) => {
      const id = query?.trim() || ''
      if (!id || !/^\d+$/.test(id)) {
        requisites.value = []
        return
      }

      requisitesLoading.value = true
      try {
        const response = await api.requisites(
          {limit: 100, offset: 0} as PaginationDTO,
          {},
          {id, isDeleted: 'false'} as RequisitesRequestDTO
        )
        requisites.value = response.items
      } catch (error) {
        requisites.value = []
      } finally {
        requisitesLoading.value = false
      }
    }

    const asyncFindRequisites = debounce((query: string) => {
      loadRequisites(query)
    }, 500)

    const loadStores = async (query: string) => {
      const id = query?.trim() || ''
      if (!id || !/^\d+$/.test(id)) {
        stores.value = []
        return
      }

      storesLoading.value = true
      try {
        const response = await api.merchantStores(
          {limit: 100, offset: 0} as PaginationDTO,
          {ids: [Number.parseInt(id, 10)]}
        )
        stores.value = response.items
      } catch (error) {
        stores.value = []
      } finally {
        storesLoading.value = false
      }
    }

    const asyncFindStores = debounce((query: string) => {
      loadStores(query)
    }, 500)

    const resetResult = () => {
      result.value = null
    }

    watch([
      () => selectedRequisite.value?.id,
      selectedStoreId,
      selectedFile,
    ], () => {
      resetResult()
    })

    const onDrop = (acceptFiles: File[], rejectReasons: any[]) => {
      if (rejectReasons?.length) {
        notification.notify({title: helper.trans('counterparty_import.invalid_file_type'), type: 'error'})
      }
      const file = acceptFiles[0]
      if (!file) {
        return
      }
      if (!file.name.toLowerCase().endsWith('.txt')) {
        notification.notify({title: helper.trans('counterparty_import.invalid_file_type'), type: 'error'})
        return
      }
      selectedFile.value = file
    }

    const dropzone = useDropzone({onDrop, accept: ['.txt', 'text/plain'], multiple: false, maxFiles: 1})

    const clearFile = () => {
      selectedFile.value = null
    }

    const reset = () => {
      selectedRequisite.value = null
      selectedStore.value = null
      selectedFile.value = null
      result.value = null
    }

    const show = (context?: { requisite?: TraderRequisitesDTO; store?: MerchantStoreDTO }) => {
      reset()
      requisites.value = []
      stores.value = []
      if (context?.requisite) {
        selectedRequisite.value = context.requisite
        requisites.value = [context.requisite]
      }
      if (context?.store) {
        selectedStore.value = context.store
        stores.value = [context.store]
      }
      isVisible.value = true
    }

    const hide = () => {
      isBusy.value = false
      loader.value?.hide()
      isVisible.value = false
      reset()
    }

    const onVisibleChange = (value: boolean) => {
      if (!value) {
        hide()
        return
      }
      isVisible.value = true
    }

    const translateReason = (reason: string): string => {
      const slotsMatch = reason.match(NOT_ENOUGH_SLOTS)
      if (slotsMatch) {
        return helper.trans('counterparty_import.reasons.not_enough_slots', {need: slotsMatch[1], free: slotsMatch[2]})
      }
      const key = REASON_KEYS[reason]
      return key ? helper.trans(key) : reason
    }

    const formatError = (error: CounterpartyImportErrorDTO): string => {
      const reason = translateReason(error.reason)
      if (!error.line && !error.userId) {
        return reason
      }
      return helper.trans('counterparty_import.line_error', {
        line: error.line,
        userId: error.userId || '—',
        reason,
      })
    }

    const httpErrorTitle = (error: any): string => {
      const status = error?.response?.status
      if (status === 403) {
        return helper.trans('counterparty_import.forbidden')
      }
      if (status === 404) {
        return helper.trans('counterparty_import.not_found')
      }
      if (status === 400) {
        return helper.trans('counterparty_import.bad_request')
      }
      return error?.response?.data?.message || helper.trans('counterparty_import.unexpected_error')
    }

    const applyResultFromError = (error: any): boolean => {
      const data = error?.response?.data
      if (data && typeof data.valid === 'boolean') {
        result.value = data as CounterpartyImportResultDTO
        return true
      }
      return false
    }

    const validateFile = async () => {
      if (!canCheck.value) {
        return
      }

      loader.value?.show()
      isBusy.value = true
      try {
        const data = allowlistEnabled.value
          ? await api.validateStoreCounterpartiesImport(selectedStoreId.value!, selectedFile.value!)
          : await api.validateRequisitesCounterpartiesImport(
            selectedRequisite.value!.id,
            selectedStoreId.value!,
            selectedFile.value!
          )
        result.value = data
      } catch (error: any) {
        if (!applyResultFromError(error)) {
          result.value = null
          notification.notify({title: httpErrorTitle(error), type: 'error'})
        }
      } finally {
        isBusy.value = false
        loader.value?.hide()
      }
    }

    const importFile = async () => {
      if (!canImport.value) {
        return
      }

      if (result.value?.toImport === 0) {
        notification.notify({
          title: importText('nothing_to_import'),
          type: 'warn',
        })
        hide()
        return
      }

      loader.value?.show()
      isBusy.value = true
      try {
        const importResult = allowlistEnabled.value
          ? await api.importStoreCounterparties(selectedStoreId.value!, selectedFile.value!)
          : await api.importRequisitesCounterparties(
            selectedRequisite.value!.id,
            selectedStoreId.value!,
            selectedFile.value!
          )
        result.value = importResult

        if (!importResult.valid) {
          return
        }

        const requisitesId = selectedRequisite.value?.id
        notification.notify({
          title: importText('imported', {n: importResult.imported}),
          type: 'success',
        })
        hide()
        if (requisitesId) {
          emit('imported', requisitesId)
        }
      } catch (error: any) {
        if (!applyResultFromError(error)) {
          notification.notify({title: httpErrorTitle(error), type: 'error'})
        }
      } finally {
        isBusy.value = false
        loader.value?.hide()
      }
    }

    return {
      isVisible,
      isBusy,
      loader,
      requisitesLoading,
      storesLoading,
      requisiteOptions,
      storeOptions,
      selectedRequisite,
      selectedStore,
      selectedStoreId,
      selectedFile,
      result,
      allowlistEnabled,
      importText,
      canCheck,
      canImport,
      visibleErrors,
      dropzone,
      helper,
      show,
      hide,
      onVisibleChange,
      asyncFindRequisites,
      asyncFindStores,
      requisiteLabel,
      storeLabel,
      clearFile,
      validateFile,
      importFile,
      formatError,
    }
  }
})
</script>
